import type React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Heading, LoadingMask, Paragraph, Tabs, TextButton } from '@zillow/constellation-10';
import { useCreateRenterPaymentMethod } from '@renter-hub/hooks/useCreatePaymentMethod';
import {
  useCreateManualPaymentWebApiHandler_createManualPayment,
  useReauthenticatePaymentMethodWebApiHandler_reauthenticateRenterPaymentMethod,
} from '@zg-rentals/ts-rent-payment-client-react-query';
import {
  usePaymentReportingData,
  usePaymentReportingOptOut,
  useRefetchCreditTrackingStatus,
} from '@renter-hub/hooks/usePaymentReporting';

import NotificationContainer from '@common/components/NotificationContainer';
import StackedNotificationsContainer from '@common/components/StackedNotificationsContainer';
import TransactionLedger from './modules/TransactionLedger';
import { MonthlyRentCard } from './modules/MonthlyRentCard/MonthlyRentCard';
import MoveInCostsCard from './modules/MoveInCostsCard/MoveInCostsCard';
import DashboardOneTimePaymentCard from './modules/DashboardOneTimePaymentCard';
import { LandlordCard } from './modules/Landlord.card';
import ManualPaymentModal from './modules/ManualPaymentModal';
import ContactUsSection from '@common/components/ContactUsSection';
import HelmetContainer from '@common/components/HelmetContainer';
import SeoConstants from '@renter-hub/constants/seoConstants';
import OnboardingStatus from '@renter-hub/constants/onboardingStatus';
import REQUEST_STATUS from '@common/constants/requestStatus';
import userPersonas from '@common/constants/userPersonas';
import { getRachelDashboard } from '@renter-hub/actions/requests';
import {
  closeNotification,
  showModalNotification,
  showStackedPageNotification,
} from '@common/actions/NotificationActions';
import { checkRequestActionSuccess, getSignInPage } from '@common/paymentsUtils/api';
import { centsToDollars } from '@common/paymentsUtils/currencyUtils';
import { getBooleanConfig } from '@zg-rentals/frontend-config/client';
import HTTP_STATUS from '@common/constants/HttpConstants';
import { DEFAULT_ERROR } from '@renter-hub/constants/notifications';
import { logError } from '@zg-rentals/log-error';
import type { ErrorInterface } from '@common/types';

import type {
  DashboardSubheadingInterface,
  LandlordProfileModel,
  LateRentModel,
  PaymentReportHistoryInterface,
  PaymentReportingInterface,
  RenterConfigInterface,
  RenterMoveInCostsDetailsInterface,
  RenterNextPayableInterface,
  RenterPaymentDetailsInterface,
  RenterPaymentMethodInterface,
  RenterProfileModel,
  RenterRentTermsInterface,
  RenterTransactionProps,
} from '@renter-hub/models/Types';
import RentReportingPage from '@renter-hub/pages/RentReportingPage/RentReportingPage.component';
import { OutstandingBalanceCard } from './modules/OutstandingBalanceCard/OutstandingBalanceCard.component';
import { PaymentReportingCard } from './modules/PaymentReportingCard/PaymentReportingCard.component';
import { PaymentReportingOptInSuccessModal } from './modules/PaymentReportingOptInSuccessModal/PaymentReportingOptInSuccessModal';
import { RewardsUpsellCard } from './modules/RewardsUpsellCard/RewardsUpsellCard';
import { RewardsSummaryCard } from './modules/RewardsSummaryCard/RewardsSummaryCard';
import { useRewardsUpsell } from '@renter-hub/hooks/useRewardsUpsell';
import { RentReportingOptOutModal } from '../RentReportingPage/components/RentReportingOptOutModal';
import ONE_TIME_PAYMENT_TYPE from '@common/constants/oneTimePaymentTypes';
import TransactionConstants from '@common/constants/TransactionConstants';
import {
  getDismissedMarkedPaymentKeys,
  MARKED_PAID_BANNER_WINDOW_MS,
} from '@renter-hub/notifications/markedPaidBannerDismissal';
import {
  PAYMENT_REPORTING_OPT_IN_SUCCESS,
  PAYMENT_REPORTING_OPT_OUT_SUCCESS,
} from '@renter-hub/constants/notifications';
import { showPageNotification } from '@common/actions/NotificationActions';
import type { MOVE_IN_COSTS_PAYMENT, RENT_PAYMENT } from '@renter-hub/constants/paymentConstants';
import * as rentReportingConstants from '@renter-hub/constants/rentReportingConstants';
import RachelCSEvents from '@renter-hub/tracking/ClickStreamEvents';
import paths from '@renter-hub/pathsManifest';
import LandlordBlocked from './modules/LandlordBlocked';
import LoadingFailed from './LoadingFailed';
import CreditTrackingLoader from './modules/CreditTrackingLoader';
import { PLAID_ACTION_STATUSES, VERIFICATION_STATUS } from '@common/constants/PlaidRequirement';

interface RenterDashboardProps {
  today: Date;
  fromEmail: boolean;
  onboardingStatus: string;
  latePaymentsAllowed: boolean;
  rentPaymentSubheading: DashboardSubheadingInterface;
  moveInCostsDetails: RenterMoveInCostsDetailsInterface;
  moveInCostsSubheading: DashboardSubheadingInterface;
  rentTerms: RenterRentTermsInterface;
  landlordProfile: LandlordProfileModel;
  paymentDetails: RenterPaymentDetailsInterface;
  paymentHistory: {
    propertyId: string;
    transactions: Array<RenterTransactionProps>;
    lateRentPayments: Array<LateRentModel>;
    unpaidOtpTransactions: Array<RenterTransactionProps>;
  };
  paymentReporting: PaymentReportingInterface;
  paymentReportHistory: PaymentReportHistoryInterface;
  notification: any;
  requestStatus: string;
  user: any;
  listingAlias: string;
  config: RenterConfigInterface;
  currentScheduleMonth: any;
  nextPayable: RenterNextPayableInterface;
  displayRentAmountChangeReminder: ({ newAmount, effectiveDueDate }) => void;
  displayExpiredRentAmountChange: ({ landlordFirstName, landlordEmail }) => void;
  displayPastDueWarningWhenPaymentPlanRequested: () => void;
  displayVerifySingleBankAccountBanner: ({
    accountLast4,
    paymenMethodStatus,
    plaidEnv,
    plaidLinkToken,
    plaidLinkTokenExpiration,
    getPlaidLinkToken,
    onSuccess,
    onExit,
  }) => void;
  displayVerifyMultipleBankAccountsBanner: ({ onManagePaymentMethodsClick }) => void;
  displayPaymentMarkedPaidByLandlordBanner: ({
    landlordFirstName,
    payments,
  }: {
    landlordFirstName: string;
    payments: Array<{ key: string; label: string; amount: string }>;
  }) => void;
  payOneTimePaymentOnClick: (otpKey: string) => void;
  payRentPaymentOnClick: (scheduleMonth: string) => void;
  goToEditPaymentInfoPage: (typeOfPaymentToMake?: typeof RENT_PAYMENT | typeof MOVE_IN_COSTS_PAYMENT) => void;
  goToManagePaymentMethodsPage: () => void;
  goToEditPaymentFrequencyPage: () => void;
  goToEditPaymentSchedulingPage: (recurring: boolean) => void;
  goToCancelPaymentPage: () => void;
  goToEditMoveInCostsPage: () => void;
  goToCancelMoveInCostsPage: () => void;
  goToPaymentReportingPage: () => void;
  paymentReportingSuccessModalClick: () => void;
  rentSubscriptionAutoCanceled: boolean;
  featureFlagEnableRentReporting: boolean;
  enablePaymentStatus: boolean;
  paymentMethods: Array<RenterPaymentMethodInterface>;
  enableCreditTracking: boolean;
  renterProfile: RenterProfileModel;
  autoOpenCreditTrackingEnroll: boolean;
  goToRentReportingPage: () => void;
  location: any;
  getPlaidUpdateModeLinkToken: (externalId?: string) => Promise<string>;
  onPaymentMethodCreationSuccess: () => Promise<any>;
  onPaymentMethodCreationError: (error) => Promise<any>;
  goToDashboard: () => void;
  trackValidationError: (...args: any) => any;
}

const getMarkedPaymentKey = (transaction: RenterTransactionProps): string =>
  `${transaction.groupKey || `${transaction.paymentCategory}:${transaction.scheduleMonth}`}:${transaction.dateCreated}`;

const isRecentlyMarked = (transaction: RenterTransactionProps): boolean => {
  const markedAt = transaction.dateCreated ? new Date(transaction.dateCreated).getTime() : NaN;
  return !Number.isNaN(markedAt) && Date.now() - markedAt <= MARKED_PAID_BANNER_WINDOW_MS;
};

const getMarkedPaymentLabel = (transaction: RenterTransactionProps): string => {
  if (transaction.paymentCategory === TransactionConstants.CATEGORY.RENT_PAYMENT) {
    const [year, month] = transaction.scheduleMonth.split('-');
    return `${new Date(Number(year), Number(month) - 1).toLocaleString('default', { month: 'long' })} rent`;
  }
  if (transaction.paymentCategory === TransactionConstants.CATEGORY.MOVE_IN_COSTS) {
    return 'move-in costs';
  }
  switch (transaction.paymentType) {
    case ONE_TIME_PAYMENT_TYPE.LATE_FEE:
      return 'late fee';
    case ONE_TIME_PAYMENT_TYPE.PET_FEE:
      return 'pet fee';
    case ONE_TIME_PAYMENT_TYPE.PAYMENT_PLAN:
      return transaction.paymentPlanInstallmentTitle || 'payment plan installment';
    default:
      return `${transaction.paymentType} payment`;
  }
};

const RenterDashboardV2 = (props: RenterDashboardProps) => {
  const {
    autoOpenCreditTrackingEnroll,
    currentScheduleMonth = {},
    enableCreditTracking,
    featureFlagEnableRentReporting,
    landlordProfile,
    latePaymentsAllowed,
    moveInCostsDetails,
    moveInCostsSubheading,
    nextPayable,
    onboardingStatus,
    paymentDetails,
    paymentHistory,
    paymentMethods,
    rentPaymentSubheading,
    rentTerms,
    requestStatus,
    today,
  } = props;
  const { rentAmountChangeRequest = {} } = rentTerms;
  const { renterAcceptanceDate, newAmount, effectiveDueDate, expired } = rentAmountChangeRequest;
  const { transactions, unpaidOtpTransactions = [] } = paymentHistory;
  const termComplete = onboardingStatus !== OnboardingStatus.TERM_COMPLETE;
  const path = props.location?.pathname || window.location.pathname;
  const rentTermPaymentsComplete = onboardingStatus === OnboardingStatus.TERM_COMPLETE && !nextPayable.dueDate;
  const isPaymentPlanRequested = transactions?.some(
    (transaction) => transaction.paymentType === ONE_TIME_PAYMENT_TYPE.PAYMENT_PLAN,
  );
  const currentTransaction = paymentHistory.transactions.find(
    (transaction) => currentScheduleMonth.scheduleMonth === transaction.scheduleMonth,
  );

  const [rentYear, rentMonthNumber] = currentTransaction?.scheduleMonth?.split('-') ?? [];
  const rentMonth =
    rentYear && rentMonthNumber
      ? new Date(Number(rentYear), Number(rentMonthNumber) - 1).toLocaleString('default', { month: 'long' })
      : '';

  const getInitialTabState = () => (path.endsWith(paths.rentReportingPage) ? 1 : 0);

  const { mutateAsync: createRenterPaymentMethod } = useCreateRenterPaymentMethod({
    onSuccess: () => props.onPaymentMethodCreationSuccess(),
    onError: (error) => props.onPaymentMethodCreationError(error),
  });

  const { mutateAsync: reauthenticatePaymentMethod } =
    useReauthenticatePaymentMethodWebApiHandler_reauthenticateRenterPaymentMethod({
      onSuccess: () => props.onPaymentMethodCreationSuccess(),
      onError: (error) => props.onPaymentMethodCreationError(error),
    });

  const dispatch = useDispatch();
  const optOutMutation = usePaymentReportingOptOut();
  const refetchCreditTrackingStatus = useRefetchCreditTrackingStatus();

  const { mutateAsync: createManualPaymentMutation } = useCreateManualPaymentWebApiHandler_createManualPayment({
    onError: (error) => {
      logError({ error });
      dispatch(showModalNotification(DEFAULT_ERROR));
    },
  });

  const { paymentReportingData, paymentReportHistoryData, isLoadingPaymentReporting, shouldShowPaymentReportingCard } =
    usePaymentReportingData({
      featureFlagEnableRentReporting,
      enableCreditTracking,
      rentTermsKey: rentTerms?.rentTermsKey,
    });

  const {
    isRewardsEnabled,
    isRewardsSupported,
    isEnrolled: isRewardsEnrolled,
    points: rewardsPoints,
    maxPoints: rewardsMaxPoints,
    cardDisplay: rewardsCardDisplay,
    claimablePoints: rewardsClaimablePoints,
    isLoading: isRewardsLoading,
  } = useRewardsUpsell();
  const showRewardsCard = isRewardsEnabled && isRewardsSupported;

  const onPlaidSubmit = async (plaidToken: any, paymentMethod) => {
    const isPlaidAuthRevoked = paymentMethod.paymentMethodStatus === VERIFICATION_STATUS.PLAID_REVOKED;
    if (isPlaidAuthRevoked) {
      await reauthenticatePaymentMethod({
        postData: {
          encodedPaymentMethodId: paymentMethod.id,
        },
      });
    } else {
      await createRenterPaymentMethod({
        postData: {
          plaidToken: {
            accountId: plaidToken.accountId,
            publicToken: plaidToken.publicToken,
          },
          bankName: plaidToken.bankName,
          mask: plaidToken.mask,
          ...(plaidToken.status && { status: plaidToken.status }),
        },
      });
    }
  };

  const onPlaidExit = (err, metadata) => {
    props.trackValidationError({
      plaidErrorsLastEncountered: err,
      plaidMetadata: metadata,
    });
    props.goToDashboard();
  };

  const createManualPaymentOnClick = async (scheduleMonthForPayment: string) => {
    try {
      await dispatch(closeNotification());

      await createManualPaymentMutation({
        postData: {
          scheduleMonthForPayment,
        },
      });

      // @ts-expect-error - getRachelDashboard types need updating
      const [renterStatusResponse] = await dispatch(getRachelDashboard({}));
      await checkRequestActionSuccess(renterStatusResponse);

      return { error: false };
    } catch (err) {
      const error = err as ErrorInterface;
      if (error.action && error.action.payload) {
        const { status } = error.action.payload;

        if (status === HTTP_STATUS.SC_UNAUTHORIZED) {
          window.location = getSignInPage();
          return null;
        }
      }
      logError({ error: err });
      dispatch(showStackedPageNotification(DEFAULT_ERROR));
      return { error: true };
    }
  };

  const [manualPayment, setManualPayment] = useState({
    showModal: false,
    scheduleMonth: '',
  });
  const [reportingOptOutModalOpen, setReportingOptOutModalOpen] = useState(false);
  const [reportingOptOutError, setReportingOptOutError] = useState(false);
  const [currentTab, setCurrentTab] = useState(getInitialTabState());

  useEffect(() => {
    const newTab = getInitialTabState();
    setCurrentTab((prev) => (prev !== newTab ? newTab : prev));
  }, [props.location?.pathname]);

  useEffect(() => {
    if (!props.enableCreditTracking && path.endsWith(paths.rentReportingPage)) {
      window.history.pushState({}, '', paths.baseUrl);
    }

    if (!termComplete && renterAcceptanceDate) {
      props.displayRentAmountChangeReminder({
        newAmount,
        effectiveDueDate,
      });
    } else if (expired) {
      props.displayExpiredRentAmountChange({
        landlordFirstName: landlordProfile.name,
        landlordEmail: landlordProfile.email,
      });
    }

    if (isPaymentPlanRequested) {
      props.displayPastDueWarningWhenPaymentPlanRequested();
    }

    if (getBooleanConfig('isLandlordMarkAsPaidOfflineEnabled', false)) {
      const dismissedKeys = getDismissedMarkedPaymentKeys();
      const markedTransactions = (transactions ?? []).filter(
        (transaction) =>
          transaction.renterStatus === TransactionConstants.STATUS.MANUAL &&
          transaction.dateReceived &&
          isRecentlyMarked(transaction) &&
          !dismissedKeys.includes(getMarkedPaymentKey(transaction)),
      );
      if (markedTransactions.length > 0) {
        props.displayPaymentMarkedPaidByLandlordBanner({
          landlordFirstName: landlordProfile.firstName || 'Your landlord',
          payments: markedTransactions.map((transaction) => ({
            key: getMarkedPaymentKey(transaction),
            label: getMarkedPaymentLabel(transaction),
            amount: centsToDollars(transaction.amount, { style: 'effectiveShort' }),
          })),
        });
      }
    }

    // Check for payment methods needing verification and show banner
    if (paymentMethods && paymentMethods.length > 0) {
      // Only bank accounts can have Plaid verification statuses
      const needsPlaidAction = (pm) =>
        pm.accountType === 'bank_account' && PLAID_ACTION_STATUSES.has(pm.paymentMethodStatus);
      const paymentMethodsNeedingAction = paymentMethods.filter(needsPlaidAction);

      if (paymentMethodsNeedingAction.length === 1) {
        const paymentMethod = paymentMethodsNeedingAction[0];
        if (
          paymentMethodsNeedingAction[0].paymentMethodStatus === VERIFICATION_STATUS.PLAID_REVOKED ||
          paymentMethodsNeedingAction[0].paymentMethodStatus === VERIFICATION_STATUS.PENDING_MANUAL.INTERNAL
        ) {
          props.displayVerifySingleBankAccountBanner({
            accountLast4: paymentMethod.accountLast4,
            paymenMethodStatus: paymentMethodsNeedingAction[0].paymentMethodStatus,
            plaidEnv: props.config.plaidEnv,
            plaidLinkToken: props.config.plaidLinkToken,
            plaidLinkTokenExpiration: props.config.plaidLinkTokenExpiration,
            getPlaidLinkToken: () => props.getPlaidUpdateModeLinkToken(paymentMethod.id),
            onSuccess: (plaidToken: any) => onPlaidSubmit(plaidToken, paymentMethod),
            onExit: onPlaidExit,
          });
        }
      } else if (paymentMethodsNeedingAction.length > 1) {
        props.displayVerifyMultipleBankAccountsBanner({
          onManagePaymentMethodsClick: props.goToManagePaymentMethodsPage,
        });
      }
    }
  }, [
    props.rentTerms,
    props.landlordProfile,
    props.onboardingStatus,
    props.nextPayable,
    props.currentScheduleMonth,
    props.paymentHistory,
  ]);

  const goToEditPaymentInfoPage = (typeOfPaymentToMake?: typeof MOVE_IN_COSTS_PAYMENT | typeof RENT_PAYMENT): void => {
    props.goToEditPaymentInfoPage(typeOfPaymentToMake);
  };

  const goToEditPaymentSchedulingPage = (): void => {
    const { paymentSubscriptionDetails } = paymentDetails;
    const { recurring = false } = paymentSubscriptionDetails;

    props.goToEditPaymentSchedulingPage(recurring);
  };

  const payLateRentPaymentOnClick = (scheduleMonth: string): void => {
    props.payRentPaymentOnClick(scheduleMonth);
  };

  const renderOneTimePaymentCards = () => {
    const getTransactionKey = (transaction) => {
      return transaction.paymentType === ONE_TIME_PAYMENT_TYPE.PAYMENT_PLAN
        ? transaction.requestKey
        : transaction.groupKey;
    };
    return unpaidOtpTransactions.map((transaction: RenterTransactionProps) => (
      <DashboardOneTimePaymentCard
        user={props.user}
        key={getTransactionKey(transaction)}
        otpKey={getTransactionKey(transaction)}
        payOneTimePaymentOnClick={(otpKey) => props.payOneTimePaymentOnClick(otpKey)}
        goToEditPaymentInfoPage={goToEditPaymentInfoPage}
        today={today}
        {...transaction}
      />
    ));
  };

  const handleTabChange = (index: number) => {
    if (index === 1) {
      props.goToRentReportingPage();
    } else {
      window.history.pushState({}, '', paths.baseUrl);
      if (props.rentTerms?.rentTermsKey) {
        refetchCreditTrackingStatus(props.rentTerms.rentTermsKey);
      }
    }
    setCurrentTab(index);
  };

  const renderSuccess = (): React.ReactNode => {
    // Only show loading if we're waiting for data that will determine tab display
    // This prevents the flash from no-tabs to tabs
    const isOnRentReportingUrl = path.endsWith(paths.rentReportingPage);
    const shouldShowLoading =
      enableCreditTracking === true &&
      isOnRentReportingUrl &&
      (isLoadingPaymentReporting || paymentReportingData?.optedIn === undefined);

    if (shouldShowLoading) {
      return <CreditTrackingLoader />;
    }

    const paymentSubscriptionDetails = paymentDetails.paymentSubscriptionDetails;
    const nextScheduledDate = paymentSubscriptionDetails && paymentSubscriptionDetails.nextScheduledDate;
    const isMICPresent = moveInCostsDetails?.items?.length > 0 && moveInCostsSubheading?.title;
    const showOutstandingBalanceCard = paymentHistory.lateRentPayments.length > 0 && latePaymentsAllowed;
    const paymentDashboardContent = (
      <>
        <div>
          {isMICPresent && (
            <MoveInCostsCard
              moveInCostsDetails={moveInCostsDetails}
              moveInCostsSubheading={moveInCostsSubheading}
              goToEditMoveInCostsPage={props.goToEditMoveInCostsPage}
              goToEditPaymentInfoPage={goToEditPaymentInfoPage}
              goToCancelMoveInCostsPage={props.goToCancelMoveInCostsPage}
            />
          )}
          {(shouldShowPaymentReportingCard || showRewardsCard) && (
            <Box
              css={{
                display: 'flex',
                flexDirection: { base: 'column', lg: 'row' },
                gap: 'default',
                marginBottom: 'loose',
              }}
            >
              {shouldShowPaymentReportingCard && (
                <PaymentReportingCard
                  paymentReportingStatus={paymentReportingData}
                  goToPaymentReportingPage={props.goToPaymentReportingPage}
                  goToRentReportingPage={props.goToRentReportingPage}
                  rentTermsKey={props.rentTerms.rentTermsKey}
                  user={props.user}
                  listingAlias={props.listingAlias}
                  onOptOutClick={() => setReportingOptOutModalOpen(true)}
                  optOutError={reportingOptOutError}
                  setCurrentTab={(tabIndex: number) => setCurrentTab(tabIndex)}
                  fetchCreditTrackingStatus={refetchCreditTrackingStatus}
                  enableCreditTracking={enableCreditTracking}
                />
              )}
              {showRewardsCard && !isRewardsLoading && !isRewardsEnrolled && rewardsCardDisplay && (
                <RewardsUpsellCard
                  title={rewardsCardDisplay.title}
                  description={rewardsCardDisplay.description}
                  ctaText={rewardsCardDisplay.ctas?.[0]?.text}
                  claimablePoints={rewardsClaimablePoints}
                />
              )}
              {showRewardsCard && !isRewardsLoading && isRewardsEnrolled && rewardsCardDisplay && (
                <RewardsSummaryCard
                  title={rewardsCardDisplay.title}
                  pointsValueDescription={rewardsCardDisplay.pointsValueDescription}
                  ctaText={rewardsCardDisplay.ctas?.[0]?.text}
                  ctaUrl={rewardsCardDisplay.ctas?.[0]?.url ?? '/rental/rewards'}
                  points={rewardsPoints}
                  maxPoints={rewardsMaxPoints}
                />
              )}
            </Box>
          )}
          <MonthlyRentCard
            currentTransaction={currentTransaction}
            nextPayable={nextPayable}
            rentMonth={rentMonth}
            landlordName={landlordProfile.name}
            rentTermPaymentsComplete={rentTermPaymentsComplete}
            paymentSubscriptionDetails={paymentDetails.paymentSubscriptionDetails}
            accountType={paymentDetails.accountType}
            rentPaymentSubheading={rentPaymentSubheading}
            goToEditPaymentFrequencyPage={props.goToEditPaymentFrequencyPage}
            goToEditPaymentSchedulingPage={goToEditPaymentSchedulingPage}
            goToCancelPaymentPage={props.goToCancelPaymentPage}
            rentSubscriptionAutoCanceled={props.rentSubscriptionAutoCanceled}
            paymentMethods={props.paymentMethods}
            goToEditPaymentInfoPage={goToEditPaymentInfoPage}
          />
          {showOutstandingBalanceCard && (
            <OutstandingBalanceCard
              payLateRentPaymentOnClick={payLateRentPaymentOnClick}
              transactions={paymentHistory.lateRentPayments}
            />
          )}
          {renderOneTimePaymentCards()}
          <>
            <Paragraph textStyle="body-bold" className="fs-exclude">
              Payment method
            </Paragraph>
            <TextButton onClick={props.goToManagePaymentMethodsPage}>Add or manage</TextButton>
          </>
          <LandlordCard {...props} />
        </div>
        <TransactionLedger
          transactions={paymentHistory.transactions}
          landlordName={landlordProfile.name}
          currentScheduleMonth={currentScheduleMonth}
          scheduledDate={nextScheduledDate}
          openManualPaymentModal={(scheduleMonth: string) => setManualPayment({ showModal: true, scheduleMonth })}
          payLateRentPaymentOnClick={payLateRentPaymentOnClick}
          showOutstandingBalanceCard={showOutstandingBalanceCard}
          enablePaymentStatus={props.enablePaymentStatus}
          paymentReportHistory={props.paymentReportHistory}
          enableCreditTracking={enableCreditTracking}
        />
        <Box css={{ marginTop: 'loose' }}>
          <div>
            <ContactUsSection />
          </div>
        </Box>
      </>
    );

    const pageContentWithCreditTracking = (
      <>
        {paymentReportingData?.optedIn ? (
          <Tabs.Root
            selected={currentTab === 0 ? 'overview' : 'rent-reporting'}
            onSelectedChange={(event) => handleTabChange(event === 'overview' ? 0 : 1)}
          >
            <Tabs.List>
              <Tabs.Tab value="overview">Overview</Tabs.Tab>
              <Tabs.Tab value="rent-reporting">Rent reporting</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="overview">
              <Box css={{ marginTop: 'looser' }}>{paymentDashboardContent}</Box>
            </Tabs.Panel>

            <Tabs.Panel value="rent-reporting">
              <RentReportingPage
                onReportingOptOut={() => setReportingOptOutModalOpen(true)}
                autoOpenCreditTrackingEnroll={autoOpenCreditTrackingEnroll}
                transactions={paymentHistory.transactions}
                paymentReporting={paymentReportingData}
                paymentReportHistory={paymentReportHistoryData}
              />
            </Tabs.Panel>
          </Tabs.Root>
        ) : (
          paymentDashboardContent
        )}
      </>
    );

    return (
      <Box
        css={{
          minHeight: '50px',
          marginBottom: 'loose',
        }}
      >
        {enableCreditTracking ? pageContentWithCreditTracking : paymentDashboardContent}
      </Box>
    );
  };

  const handleCreditReportingOptOut = async () => {
    try {
      await optOutMutation.mutateAsync({
        postData: { rentTermsKey: props.rentTerms.rentTermsKey ?? '' },
      });
      dispatch(showPageNotification(PAYMENT_REPORTING_OPT_OUT_SUCCESS));
    } catch {
      setReportingOptOutError(true);
    }
    setReportingOptOutModalOpen(false);
    handleTabChange(0);
    RachelCSEvents.emitOptOutOfRentReportingEvent({
      user: props.user,
      currentProperty: { listingAlias: props.listingAlias },
    });
  };

  let body;

  if (onboardingStatus === OnboardingStatus.REJECTED) {
    body = <LandlordBlocked rentSubscriptionAutoCanceled={props.rentSubscriptionAutoCanceled} />;
  } else if (requestStatus === REQUEST_STATUS.SUCCESS) {
    body = renderSuccess();
  } else {
    body = <LoadingFailed />;
  }

  return (
    <>
      <Heading level={1} textStyle="heading-md" css={{ marginBottom: 'default' }}>
        Your payments
      </Heading>
      <HelmetContainer
        {...SeoConstants.dashboardPage}
        scripts={rentReportingConstants.getCreditTrackingScripts(enableCreditTracking)}
      />
      <Box css={{ '&:not(:empty)': { marginBottom: 'loose' } }}>
        <NotificationContainer persona={userPersonas.RENTER} />
      </Box>
      <StackedNotificationsContainer persona={userPersonas.RENTER} />
      <LoadingMask
        loading={!requestStatus || requestStatus === REQUEST_STATUS.INIT}
        css={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)' }}
      >
        {body}
      </LoadingMask>
      {manualPayment.showModal && (
        <ManualPaymentModal
          createManualPaymentOnClick={() => createManualPaymentOnClick(manualPayment.scheduleMonth)}
          closeManualPaymentModal={() => setManualPayment({ showModal: false, scheduleMonth: '' })}
        />
      )}
      {props.featureFlagEnableRentReporting && props.notification?.modal?.name === PAYMENT_REPORTING_OPT_IN_SUCCESS && (
        <PaymentReportingOptInSuccessModal onClose={props.paymentReportingSuccessModalClick} />
      )}
      {props.featureFlagEnableRentReporting && (
        <RentReportingOptOutModal
          isOpen={reportingOptOutModalOpen}
          onClose={() => setReportingOptOutModalOpen(false)}
          onOptOut={handleCreditReportingOptOut}
        />
      )}
    </>
  );
};

export default RenterDashboardV2;
